import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { t } from 'ttag'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { ProgressBar } from '@h2/components/display/progressBar'
import { Globals } from '@h2/globals'

const ProgressBarWrap = styled.div`
  padding: 0 25px;
`
const DragAndDropOuter = styled.div`
  position: relative;
`
const ActiveDraggingWrapper = styled.div`
  border: dashed #000 2px;
  padding: 6px;
  background-color: rgba(229, 241, 204, 0.9);
  border-radius: 4px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
`
const ActiveDraggingContent = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
  left: 15px;
  color: grey;
  font-size: 36px;
`
const FileDragAndDrop = ({ uploadProgress, uploadHandler, children, isUploading }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const [numFiles, setNumFiles] = useState(0)
  const dragAndDropContainer = useRef(null)
  const { user, allowUserFiles, isSubscriber } = useContext(Globals)

  useEffect(() => {
    setIsDragging(dragCounter > 0)

    if (!isDragging) {
      setNumFiles(0)
    }
  }, [dragCounter, isDragging])
  const handleDrag = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
  }, [])
  const handleDragIn = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      const allowFiles =
        isSubscriber || user?.hasFileAccess || user?.isRoot || allowUserFiles

      if (
        allowFiles &&
        event.dataTransfer.types &&
        event.dataTransfer.types.includes('Files')
      ) {
        setDragCounter(dragCounter + 1)
        setNumFiles(event.dataTransfer.items.length)
      }
    },
    [isSubscriber, allowUserFiles, dragCounter, user]
  )
  const handleDragOut = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      setDragCounter(Math.max(0, dragCounter - 1))
    },
    [dragCounter]
  )
  const handleDrop = useCallback(
    async event => {
      event.preventDefault()
      event.stopPropagation()

      if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
        const itms = [...event.dataTransfer.items]
          .filter(i => !i.webkitGetAsEntry()?.isDirectory)
          .map(i => i.getAsFile())

        if (itms.length > 0) {
          const formData = new FormData()

          Array.from(itms).forEach(file => {
            formData.append('file', file)
          })
          setDragCounter(prevVal => prevVal + 1)
          await uploadHandler(formData)
        }

        setDragCounter(0)
      } else if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        //this is an older spec, but doesn't support folder detection
        await uploadHandler(event.dataTransfer.files)
        setDragCounter(0)
      }

      event.dataTransfer.clearData()
    },
    [uploadHandler]
  )

  useEffect(() => {
    const referencedContainer = dragAndDropContainer?.current

    if (referencedContainer) {
      referencedContainer.addEventListener('dragenter', handleDragIn)
      referencedContainer.addEventListener('dragleave', handleDragOut)
      referencedContainer.addEventListener('dragover', handleDrag)
      referencedContainer.addEventListener('drop', handleDrop)
    }

    return () => {
      if (referencedContainer) {
        referencedContainer.removeEventListener('dragenter', handleDragIn)
        referencedContainer.removeEventListener('dragleave', handleDragOut)
        referencedContainer.removeEventListener('dragover', handleDrag)
        referencedContainer.removeEventListener('drop', handleDrop)
      }
    }
  }, [dragAndDropContainer, handleDragIn, handleDragOut, handleDrag, handleDrop])

  const uploadProgressBar = () => {
    if (isUploading) {
      return (
        <ProgressBarWrap>
          <ProgressBar display="green" value={uploadProgress} />
        </ProgressBarWrap>
      )
    }

    return <div></div>
  }
  const fileCountText = numFiles > 1 ? t`Your ${numFiles} Files` : t`Your File`
  const fileCountSubject = numFiles > 1 ? t`They` : t`It`

  return (
    <DragAndDropOuter ref={dragAndDropContainer}>
      {(isDragging || isUploading) && (
        <ActiveDraggingWrapper>
          <ActiveDraggingContent>
            <Heading align="center" level="2" style={{ margin: '5px' }}>
              {isUploading ? t`Uploading, please wait...` : t`Drop ${fileCountText} Here`}
            </Heading>
            {!isUploading && (
              <Text align="center" size="large">
                {t`${fileCountSubject} will be uploaded and added to your files`}
              </Text>
            )}
            {uploadProgressBar()}
          </ActiveDraggingContent>
        </ActiveDraggingWrapper>
      )}
      {children}
    </DragAndDropOuter>
  )
}

FileDragAndDrop.propTypes = {
  uploadProgress: PropTypes.number,
  uploadHandler: PropTypes.func,
  children: PropTypes.object,
  isUploading: PropTypes.bool
}

export { FileDragAndDrop }
